/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { LAYOUT2 } from '@/Client/Styles';
import { MODAL_EFFECTS } from './ModalStyleLib';

const OVERLAY_STYLE = css`
  label: OVERLAY_STYLE;
  position: fixed;
  inset: 0;
  background: ${MODAL_EFFECTS.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 3000;
`;

const OVERLAY_FULL_SCREEN_STYLE = css`
  label: OVERLAY_FULL_SCREEN_STYLE;
  padding: ${LAYOUT2.spacing1p5}px;
`;

const CONTENT_STYLE = (maxWidth: string) =>
  css`
  label: CONTENT_STYLE;
  width: 100%;
  max-width: ${maxWidth};
  max-height: calc(100vh - 40px);
  overflow-y: auto;
`;

const CONTENT_FULL_SCREEN_STYLE = css`
  label: CONTENT_FULL_SCREEN_STYLE;
  height: 100%;
  max-width: none;
  max-height: 100%;
  overflow: hidden;
`;

type ModalShellProps = {
  visible: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Wider modals (e.g. data tables); defaults to 700px */
  contentMaxWidth?: string;
  /** Stretch to the full viewport, minus a small margin on all four sides. */
  fullScreen?: boolean;
  /** Set false to keep clicks on the backdrop from closing the modal. */
  closeOnBackdropClick?: boolean;
};

export const ModalShell = ({
  visible,
  onClose,
  children,
  contentMaxWidth = '700px',
  fullScreen = false,
  closeOnBackdropClick = true,
}: ModalShellProps) => {
  if (!visible) return null;

  // Portal to <body> so the overlay escapes any ancestor stacking context (e.g. the
  // position:fixed page layout) and its z-index competes at the document root —
  // otherwise the left nav sidebar paints over it despite the modal's higher z-index.
  return createPortal(
    <div
      css={[OVERLAY_STYLE, fullScreen && OVERLAY_FULL_SCREEN_STYLE]}
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div
        css={[CONTENT_STYLE(contentMaxWidth), fullScreen && CONTENT_FULL_SCREEN_STYLE]}
        onClick={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};
